/**
 * Contract for the Help Center / knowledge base (docs/kb.md).
 * Imports from api.ts are type-only: api.ts spreads `KB_ROUTES` into `ROUTES`.
 *
 * Not to be confused with "knowledge" (knowledge.ts): agent memory, the Obsidian vault, master chat.
 */
import { z } from 'zod';
import type { RouteDef } from './api.js';

export const KB_LOCALES = ['nl', 'en'] as const;
export const KbLocale = z.enum(KB_LOCALES);
export type KbLocale = (typeof KB_LOCALES)[number];
export const KB_DEFAULT_LOCALE: KbLocale = 'en';

/** Who changed something: `user:<id>`, `token:<id>` or `system` (seed). */
export type KbActor = `user:${string}` | `token:${string}` | 'system';

export const KB_ARTICLE_TYPES = ['article', 'tutorial', 'faq'] as const;
export const KbArticleType = z.enum(KB_ARTICLE_TYPES);
export type KbArticleType = z.infer<typeof KbArticleType>;
export const KB_ARTICLE_STATUSES = ['draft', 'published', 'archived'] as const;
export const KbArticleStatus = z.enum(KB_ARTICLE_STATUSES);
export type KbArticleStatus = z.infer<typeof KbArticleStatus>;

/** Management token prefix (`afk_`), distinct from personal access tokens (`afp_`). */
export const KB_TOKEN_PREFIX = 'afk_';
export const KB_TOKEN_SCOPES = ['read', 'draft', 'write'] as const;
export const KbTokenScope = z.enum(KB_TOKEN_SCOPES);
export type KbTokenScope = z.infer<typeof KbTokenScope>;

// ---------- Limits (token efficiency + abuse protection) ----------
export const KB_LIMITS = {
  queryMax: 300,
  titleMax: 200,
  excerptMax: 400,
  bodyMax: 60_000,
  chatTurnsSent: 6,
  chatMessageMax: 1000,
  answerSources: 6,
  contextChunks: 12,
  answerMaxOutputTokens: 350,
  chatMaxOutputTokens: 1200,
  chatMaxSteps: 4,
  chatToolCalls: 6,
  chatArticleChars: 6000,
  /** Max characters of chunk text handed to the model per request (~3k tokens). */
  contextChars: 9000,
  perUserAnswersPerHour: 30,
  perUserChatPerHour: 60,
} as const;

const Id = z.string().min(1).max(64);
const SlugStr = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits and dashes');

// ---------- Reading ----------
export const KbCategoryDto = z.object({
  id: Id,
  slug: z.string(),
  position: z.number().int(),
  title: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  articleCount: z.number().int(),
});
export type KbCategoryDto = z.infer<typeof KbCategoryDto>;

export const KbArticleSummary = z.object({
  id: Id,
  slug: z.string(),
  type: KbArticleType,
  title: z.string(),
  excerpt: z.string(),
  tags: z.array(z.string()),
  categorySlug: z.string().nullable(),
  /** The article exists in the other language only (shown with a notice). */
  locale: KbLocale,
  publishedAt: z.string().nullable(),
});
export type KbArticleSummary = z.infer<typeof KbArticleSummary>;

export const KbArticleDto = KbArticleSummary.extend({
  body: z.string(),
  availableLocales: z.array(KbLocale),
  updatedAt: z.string(),
});
export type KbArticleDto = z.infer<typeof KbArticleDto>;

export const KbSearchHit = KbArticleSummary.extend({
  /** Best matching passage: plain text, matches wrapped in « » (never HTML). */
  snippet: z.string(),
  heading: z.string(),
  score: z.number(),
});
export type KbSearchHit = z.infer<typeof KbSearchHit>;

export const KbSearchQuery = z.object({
  q: z.string().trim().min(1).max(KB_LIMITS.queryMax),
  locale: KbLocale.default(KB_DEFAULT_LOCALE),
  category: z.string().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(20).default(8),
  /** '0' while typing (search-as-you-type), '1' when submitted: only submitted searches feed the content-gap report. */
  track: z.enum(['0', '1']).default('1'),
});
export type KbSearchQuery = z.infer<typeof KbSearchQuery>;

export const KbListQuery = z.object({
  locale: KbLocale.default(KB_DEFAULT_LOCALE),
  /** A category slug, or `none` for articles without one. */
  category: z.string().max(80).optional(),
  type: KbArticleType.optional(),
});
export type KbListQuery = z.infer<typeof KbListQuery>;

export const KbArticleQuery = z.object({ locale: KbLocale.default(KB_DEFAULT_LOCALE) });

// ---------- AI answer + chat (SSE) ----------
export const KbAnswerInput = z.object({
  q: z.string().trim().min(1).max(KB_LIMITS.queryMax),
  locale: KbLocale.default(KB_DEFAULT_LOCALE),
});
export type KbAnswerInput = z.infer<typeof KbAnswerInput>;

export const KbChatInput = z.object({
  conversationId: z.uuid().optional(),
  locale: KbLocale.default(KB_DEFAULT_LOCALE),
  messages: z
    .array(
      z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().trim().min(1).max(KB_LIMITS.chatMessageMax),
      }),
    )
    .min(1)
    .max(40),
});
export type KbChatInput = z.infer<typeof KbChatInput>;

export const KbSource = z.object({
  n: z.number().int(),
  slug: z.string(),
  title: z.string(),
  heading: z.string(),
});
export type KbSource = z.infer<typeof KbSource>;

export type KbIncompleteReason = 'length' | 'step_exhaustion' | 'error' | 'abort' | 'empty' | 'eof';

/** Events on the `/api/kb/answer` and `/api/kb/chat` SSE streams. */
export type KbStreamEvent =
  | { type: 'sources'; sources: KbSource[]; answerKey: string | null }
  | { type: 'text'; text: string }
  | { type: 'done'; cached: boolean; costUsd: number }
  | { type: 'incomplete'; reason: KbIncompleteReason; costUsd: number }
  /** AI unavailable (no key, limit or budget): the client falls back to the search results. */
  | { type: 'unavailable'; reason: 'no_key' | 'rate_limited' | 'budget' | 'error' };

export const KbHelpChatQuery = z.object({
  days: z.coerce.number().int().min(1).max(90).default(30),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  before: z.uuid().optional(),
  status: z.enum(['started', 'answered', 'unavailable', 'interrupted']).optional(),
  q: z.string().trim().min(1).max(200).optional(),
});
export type KbHelpChatQuery = z.infer<typeof KbHelpChatQuery>;
export const KbHelpConversationQuery = z.object({
  after: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const KbFeedbackInput = z
  .object({
    articleId: Id.optional(),
    answerKey: z.string().max(64).optional(),
    helpful: z.boolean(),
    comment: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.articleId || v.answerKey, 'articleId or answerKey is required');
export type KbFeedbackInput = z.infer<typeof KbFeedbackInput>;

// ---------- Management (platform admin or afk_ token) ----------
const LocaleContent = z.object({
  title: z.string().trim().min(1).max(KB_LIMITS.titleMax),
  excerpt: z.string().trim().max(KB_LIMITS.excerptMax).default(''),
  body: z.string().max(KB_LIMITS.bodyMax),
});
export type KbLocaleContent = z.infer<typeof LocaleContent>;

export const KbArticleInput = z.object({
  slug: SlugStr,
  categorySlug: SlugStr.nullable().default(null),
  type: KbArticleType.default('article'),
  tags: z.array(z.string().trim().min(1).max(40)).max(12).default([]),
  position: z.number().int().min(0).max(10_000).default(0),
  locales: z
    .object({ nl: LocaleContent.optional(), en: LocaleContent.optional() })
    .refine((l) => l.nl || l.en, {
      message: 'at least one locale (nl or en) is required',
    }),
  /** Publish immediately (default: draft). */
  publish: z.boolean().default(false),
});
export type KbArticleInput = z.infer<typeof KbArticleInput>;

export const KbArticleUpdate = z.object({
  slug: SlugStr.optional(),
  categorySlug: SlugStr.nullable().optional(),
  type: KbArticleType.optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(12).optional(),
  position: z.number().int().min(0).max(10_000).optional(),
  /** Locales present here are replaced; omitted locales are left alone. */
  locales: z.object({ nl: LocaleContent.optional(), en: LocaleContent.optional() }).optional(),
});
export type KbArticleUpdate = z.infer<typeof KbArticleUpdate>;

export const KbCategoryInput = z.object({
  title: z.object({ nl: z.string().trim().min(1).max(100), en: z.string().trim().min(1).max(100) }),
  description: z
    .object({ nl: z.string().max(300).optional(), en: z.string().max(300).optional() })
    .default({}),
  icon: z.string().max(40).nullable().default(null),
  position: z.number().int().min(0).max(1000).default(0),
});
export type KbCategoryInput = z.infer<typeof KbCategoryInput>;

export const KbUsageQuery = z.object({ days: z.coerce.number().int().min(1).max(90).default(30) });
export type KbUsageQuery = z.infer<typeof KbUsageQuery>;

/** One UTC day of Help Center AI spend (every day in the range is present, zero when quiet). */
export const KbUsageDay = z.object({
  day: z.string(),
  answerUsd: z.number(),
  chatUsd: z.number(),
  calls: z.number().int(),
  cacheHits: z.number().int(),
});
export type KbUsageDay = z.infer<typeof KbUsageDay>;

export const KbUsageDto = z.object({ dailyBudgetUsd: z.number(), days: z.array(KbUsageDay) });
export type KbUsageDto = z.infer<typeof KbUsageDto>;

export const KbManageListQuery = z.object({
  status: KbArticleStatus.optional(),
  /** A category slug, or `none` for articles without one. */
  category: z.string().max(80).optional(),
  q: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type KbManageListQuery = z.infer<typeof KbManageListQuery>;

export const KbManageArticleDto = z.object({
  id: Id,
  slug: z.string(),
  categorySlug: z.string().nullable(),
  type: KbArticleType,
  status: KbArticleStatus,
  tags: z.array(z.string()),
  position: z.number().int(),
  locales: z.object({
    nl: LocaleContent.nullable(),
    en: LocaleContent.nullable(),
  }),
  publishedAt: z.string().nullable(),
  updatedAt: z.string(),
  updatedBy: z.string(),
});
export type KbManageArticleDto = z.infer<typeof KbManageArticleDto>;

/** Uncategorized article + the deterministic suggestion (packages/kb/src/categorize.ts). */
export const KbCategorySuggestionDto = z.object({
  id: Id,
  slug: z.string(),
  status: KbArticleStatus,
  title: z.string(),
  suggestion: z.object({ slug: z.string(), confidence: z.number() }).nullable(),
});
export type KbCategorySuggestionDto = z.infer<typeof KbCategorySuggestionDto>;
export const KbCategorizeInput = z.object({
  items: z
    .array(z.object({ article: z.string().min(1).max(80), categorySlug: SlugStr }))
    .min(1)
    .max(100),
});
export type KbCategorizeInput = z.infer<typeof KbCategorizeInput>;

export const KbRevisionDto = z.object({
  id: Id,
  action: z.string(),
  actor: z.string(),
  createdAt: z.string(),
  snapshot: z.record(z.string(), z.unknown()),
});
export type KbRevisionDto = z.infer<typeof KbRevisionDto>;

export const KbQueriesQuery = z.object({
  /** Only queries without any result (content gaps). */
  unanswered: z.coerce.boolean().default(false),
  days: z.coerce.number().int().min(1).max(90).default(30),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type KbQueriesQuery = z.infer<typeof KbQueriesQuery>;

export const KbTokenInput = z.object({
  name: z.string().trim().min(1).max(80),
  scope: KbTokenScope.default('read'),
  expiresInDays: z.number().int().min(1).max(365).nullable().default(null),
});
export type KbTokenInput = z.infer<typeof KbTokenInput>;

export interface KbTokenDto {
  id: string;
  name: string;
  prefix: string;
  scope: KbTokenScope;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}
export interface KbTokenCreatedDto extends KbTokenDto {
  /** Shown once. */
  token: string;
}

// ---------- Routes (spread into ROUTES) ----------
// `perm: null`: any logged-in user, published content only. The `/manage` routes and the MCP
// endpoint are `public` at the route layer on purpose: they authenticate themselves (platform
// admin session + 2FA, or an `afk_` token) and answer 404 to everyone else.
export const KB_ROUTES = {
  kbListCategories: { method: 'GET', path: '/api/kb/categories', perm: null },
  kbListArticles: { method: 'GET', path: '/api/kb/articles', perm: null },
  kbGetArticle: { method: 'GET', path: '/api/kb/articles/:slug', perm: null },
  kbSearch: { method: 'GET', path: '/api/kb/search', perm: null },
  kbAnswer: { method: 'POST', path: '/api/kb/answer', perm: null },
  kbChat: { method: 'POST', path: '/api/kb/chat', perm: null },
  kbFeedback: { method: 'POST', path: '/api/kb/feedback', perm: null },

  kbManageStatus: { method: 'GET', path: '/api/kb/manage/status', perm: 'public' },
  kbManageSearch: { method: 'GET', path: '/api/kb/manage/search', perm: 'public' },
  kbManageListArticles: { method: 'GET', path: '/api/kb/manage/articles', perm: 'public' },
  kbManageGetArticle: { method: 'GET', path: '/api/kb/manage/articles/:id', perm: 'public' },
  kbManageCreateArticle: { method: 'POST', path: '/api/kb/manage/articles', perm: 'public' },
  kbManageUpdateArticle: { method: 'PATCH', path: '/api/kb/manage/articles/:id', perm: 'public' },
  kbManageCategorySuggestions: { method: 'GET', path: '/api/kb/manage/categorize', perm: 'public' },
  kbManageCategorize: { method: 'POST', path: '/api/kb/manage/categorize', perm: 'public' },
  kbManagePublishArticle: { method: 'POST', path: '/api/kb/manage/articles/:id/publish', perm: 'public' },
  kbManageArchiveArticle: { method: 'POST', path: '/api/kb/manage/articles/:id/archive', perm: 'public' },
  kbManageDeleteArticle: { method: 'DELETE', path: '/api/kb/manage/articles/:id', perm: 'public' },
  kbManageReindexArticle: { method: 'POST', path: '/api/kb/manage/articles/:id/reindex', perm: 'public' },
  kbManageListRevisions: { method: 'GET', path: '/api/kb/manage/articles/:id/revisions', perm: 'public' },
  kbManageRestoreRevision: {
    method: 'POST',
    path: '/api/kb/manage/articles/:id/revisions/:revisionId/restore',
    perm: 'public',
  },
  kbManageListCategories: { method: 'GET', path: '/api/kb/manage/categories', perm: 'public' },
  kbManageUpsertCategory: { method: 'PUT', path: '/api/kb/manage/categories/:slug', perm: 'public' },
  /** Its articles stay, without a category (FK set null); audited in the owner org. */
  kbManageDeleteCategory: { method: 'DELETE', path: '/api/kb/manage/categories/:slug', perm: 'public' },
  /** AI spend per UTC day × purpose (Settings → Help Center → Usage). */
  kbManageUsage: { method: 'GET', path: '/api/kb/manage/usage', perm: 'public' },
  kbManageQueries: { method: 'GET', path: '/api/kb/manage/queries', perm: 'public' },
  kbManageHelpChats: { method: 'GET', path: '/api/kb/manage/help-chats', perm: 'public' },
  kbManageHelpConversation: { method: 'GET', path: '/api/kb/manage/help-chats/:id', perm: 'public' },
  kbManageFeedback: { method: 'GET', path: '/api/kb/manage/feedback', perm: 'public' },
  kbManageListTokens: { method: 'GET', path: '/api/kb/manage/tokens', perm: 'public' },
  kbManageCreateToken: { method: 'POST', path: '/api/kb/manage/tokens', perm: 'public' },
  kbManageRevokeToken: { method: 'DELETE', path: '/api/kb/manage/tokens/:id', perm: 'public' },

  kbMcpEndpoint: { method: 'POST', path: '/api/kb/mcp', perm: 'public' },
  kbMcpEndpointGet: { method: 'GET', path: '/api/kb/mcp', perm: 'public' },
} as const satisfies Record<string, RouteDef>;
